"use client";

import { motion } from "motion/react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AlertTriangle, ArrowLeft } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

interface ProfileErrorPageProps {
  statusCode?: number;
  errorType?: "not-found" | "server-error" | "unauthorized" | "generic";
}

export default function ProfileErrorPage({
  statusCode = 404,
  errorType = "not-found",
}: ProfileErrorPageProps) {
  const navigate = useNavigate();
  const { t } = useTranslation(["profile", "common"]);

  const getErrorTitle = () => {
    switch (errorType) {
      case "not-found":
        return t("profile:error.notFoundTitle");
      case "server-error":
        return t("profile:error.serverErrorTitle");
      case "unauthorized":
        return t("profile:error.unauthorizedTitle");
      default:
        return t("profile:error.genericErrorTitle");
    }
  };

  const getErrorDescription = () => {
    switch (errorType) {
      case "not-found":
        return t("profile:error.notFoundDescription");
      case "server-error":
        return t("profile:error.serverErrorDescription");
      case "unauthorized":
        return t("profile:error.unauthorizedDescription");
      default:
        return t("profile:error.genericErrorDescription");
    }
  };

  return (
    <div className="bg-background flex min-h-[calc(100dvh-4rem)] w-full items-center justify-center p-4">
      <Card className="bg-foreground mx-auto w-full max-w-2xl rounded-lg border-none p-20 shadow-lg">
        <CardContent className="flex flex-col items-center p-8 text-center">
          <motion.div
            animate={{ scale: 1, opacity: 1 }}
            className="bg-primary/10 mb-6 flex h-24 w-24 items-center justify-center rounded-full"
            initial={{ scale: 0.8, opacity: 0 }}
            transition={{ duration: 0.5, type: "spring" }}
          >
            <AlertTriangle className="text-primary h-12 w-12" />
          </motion.div>

          <motion.div
            animate={{ opacity: 1, y: 0 }}
            className="space-y-4"
            initial={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div className="space-y-2">
              <h1 className="text-accent-foreground text-3xl font-bold">{getErrorTitle()}</h1>
              <p className="text-muted-foreground">
                {statusCode && <span className="font-mono">{statusCode} - </span>}
                {getErrorDescription()}
              </p>
            </div>

            <Button
              className="mt-6 flex w-full items-center gap-2"
              variant="default"
              onClick={() => navigate(-1)}
            >
              <ArrowLeft className="h-4 w-4" />
              {t("common:goBack")}
            </Button>
          </motion.div>
        </CardContent>
      </Card>
    </div>
  );
}
